/** The workflows index: every automation, its trigger, and how it last ran. */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { EmptyState, ErrorNotice, SkeletonRows, relativeTime } from '@/shared/components'
import { Constellation } from '@/shared/components/Constellation'
import { Icon } from '@/shared/components/icons'
import { useToast } from '@/shared/components/toast'
import { useRecentRuns, useWorkflowStatus, useWorkflowMutations, useWorkflows } from './api'
import { NodeIcon } from './canvas/icons'
import { pillFor } from './editor/RunPanel'
import { DEFAULT_SETTINGS, type WorkflowSummary } from './types'
import { TEMPLATES } from './templates'
import './workflows.css'

const TRIGGER_LABEL: Record<string, { label: string; icon: string }> = {
  'trigger.manual': { label: 'Manual / API', icon: 'play' },
  'trigger.callable': { label: 'Callable', icon: 'function' },
  'trigger.webhook': { label: 'Webhook', icon: 'webhook' },
  'trigger.schedule': { label: 'Schedule', icon: 'clock' },
  'trigger.app': { label: 'App event', icon: 'bolt' },
}

export function WorkflowsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const list = useWorkflows()
  const runs = useRecentRuns()
  const status = useWorkflowStatus()
  const { create, remove, duplicate, update } = useWorkflowMutations()
  const [q, setQ] = useState('')
  const [picking, setPicking] = useState(false)

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (list.data?.workflows ?? []).filter((w) => !needle || w.name.toLowerCase().includes(needle) || w.tags.some((t) => t.toLowerCase().includes(needle)))
  }, [list.data, q])

  async function createFrom(templateKey: string | null) {
    const t = templateKey ? TEMPLATES.find((x) => x.key === templateKey) : null
    try {
      const res = await create.mutateAsync({
        name: t?.name ?? 'Untitled workflow',
        description: t?.description ?? null,
        active: false,
        nodes: t?.nodes ?? [],
        edges: t?.edges ?? [],
        variables: t?.variables ?? {},
        settings: DEFAULT_SETTINGS,
        tags: t?.tags ?? [],
      })
      navigate(`/workflows/${res.workflow.workflow_id}`)
    } catch (e) {
      toast.error(e)
    }
  }

  return (
    <div className="page">
      <div className="page-header hero">
        <Constellation />
        <div className="page-header-row">
          <div>
            <h1>Workflows</h1>
            <p>
              Automations over your connected apps. Start from a call, a webhook or a clock; branch, loop and
              transform; hand one node's output to the next.
            </p>
          </div>
          <div className="row">
            <button type="button" className="btn btn-secondary" onClick={() => setPicking((v) => !v)}>
              From a template
            </button>
            <button type="button" className="btn btn-primary" onClick={() => createFrom(null)} disabled={create.isPending}>
              New workflow
            </button>
          </div>
        </div>
      </div>

      {picking && (
        <section className="wf-templates" aria-label="Templates">
          {TEMPLATES.map((t) => (
            <button key={t.key} type="button" className="wf-template" onClick={() => createFrom(t.key)}>
              <strong>{t.name}</strong>
              <span className="muted small">{t.description}</span>
              <span className="wf-template-shape mono small">{t.shape}</span>
            </button>
          ))}
        </section>
      )}

      <div className="grid-12 section">
        <section className="panel col-8">
          <div className="panel-head">
            <h2 className="panel-title">All workflows</h2>
            <div className="toolbar">
              <label className="search">
                <Icon.search className="icon" />
                <span className="visually-hidden">Search workflows</span>
                <input type="search" value={q} placeholder="Search by name or tag" onChange={(e) => setQ(e.target.value)} />
              </label>
            </div>
          </div>
          <div className="panel-body">
            {list.isLoading && <div style={{ padding: 16 }}><SkeletonRows rows={4} columns={5} /></div>}
            {list.error ? <div style={{ padding: 16 }}><ErrorNotice error={list.error} onRetry={() => list.refetch()} /></div> : null}
            {list.data && rows.length === 0 && (
              <EmptyState
                title={q ? 'No workflow matches' : 'No workflows yet'}
                icon={<NodeIcon name="workflow" />}
                action={!q && <button type="button" className="btn btn-primary" onClick={() => createFrom(null)}>Create the first one</button>}
              >
                {q ? 'Try another name or tag.' : 'A workflow is a trigger, then the steps that run when it fires. Build one from scratch or start from a template.'}
              </EmptyState>
            )}
            {rows.length > 0 && (
              <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Trigger</th>
                      <th>Status</th>
                      <th>Last run</th>
                      <th>Runs</th>
                      <th>Updated</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((w) => (
                      <WorkflowRow
                        key={w.workflow_id}
                        w={w}
                        onToggle={() => update.mutate({ id: w.workflow_id, body: { active: !w.active } })}
                        onDuplicate={() => duplicate.mutate(w.workflow_id)}
                        onDelete={() => { if (window.confirm(`Delete "${w.name}" and its runs?`)) remove.mutate(w.workflow_id) }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="panel-foot">
            <span>{rows.length} workflow{rows.length === 1 ? '' : 's'}</span>
            <span>
              {!status.data ? 'Checking storage…' : !status.data.configured ? 'MongoDB is not configured — set MONGO_URL' : status.data.reachable ? `MongoDB · ${status.data.database}` : `MongoDB unreachable: ${status.data.error ?? 'check the cluster'}`}
            </span>
          </div>
        </section>

        <section className="panel col-4">
          <div className="panel-head">
            <h2 className="panel-title">Recent runs</h2>
          </div>
          <div className="panel-body">
            {(runs.data?.runs ?? []).length === 0 ? (
              <EmptyState title="Nothing has run yet" icon={<NodeIcon name="play" />}>
                Runs from every workflow land here as they happen.
              </EmptyState>
            ) : (
              <div className="wf-recent">
                {(runs.data?.runs ?? []).slice(0, 25).map((r) => (
                  <Link key={r.run_id} to={`/workflows/${r.workflow_id}?run=${r.run_id}`} className="wf-recent-row">
                    <span className={`pill ${pillFor(r.status)}`}>{r.status}</span>
                    <span className="wf-recent-name">
                      <span>{r.workflow_name}</span>
                      <span className="muted small">{r.trigger} · {relativeTime(r.started_at)}</span>
                    </span>
                    <span className="mono small muted">{r.duration_ms} ms</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function WorkflowRow({ w, onToggle, onDuplicate, onDelete }: { w: WorkflowSummary; onToggle: () => void; onDuplicate: () => void; onDelete: () => void }) {
  const trig = w.trigger ? TRIGGER_LABEL[w.trigger] : null
  const next = w.trigger_state.schedule?.next_run_at
  return (
    <tr>
      <td>
        <Link to={`/workflows/${w.workflow_id}`} style={{ fontWeight: 600, color: 'inherit' }}>{w.name}</Link>
        {w.description && <div className="muted small">{w.description}</div>}
        {w.tags.length > 0 && <div className="row-tight" style={{ marginTop: 4 }}>{w.tags.map((t) => <span key={t} className="badge badge-neutral">{t}</span>)}</div>}
      </td>
      <td>
        {trig ? (
          <span className="row-tight">
            <NodeIcon name={trig.icon} className="icon" style={{ width: 16, height: 16 }} />
            <span>{trig.label}</span>
          </span>
        ) : (
          <span className="muted">none</span>
        )}
        {w.trigger === 'trigger.webhook' && w.trigger_state.webhook && <div className="mono small muted">/hooks/{w.trigger_state.webhook.path}</div>}
        {w.trigger === 'trigger.schedule' && next && <div className="small muted">next {relativeTime(next).replace('ago', '').trim() || 'soon'}</div>}
        {w.trigger === 'trigger.app' && w.trigger_state.app && (
          <div className="small muted">
            every {w.trigger_state.app.minutes} min{w.trigger_state.app.last_error ? ' · last check failed' : w.trigger_state.app.last_polled_at ? ` · checked ${relativeTime(w.trigger_state.app.last_polled_at)}` : ''}
          </div>
        )}
      </td>
      <td>
        <button type="button" className={`pill ${w.active ? 'pill-success' : ''}`} onClick={onToggle} title={w.active ? 'Deactivate' : 'Activate'} style={{ cursor: 'pointer', border: 0 }}>
          {w.active ? 'Active' : 'Inactive'}
        </button>
      </td>
      <td>
        {w.last_run_status ? (
          <span className="row-tight">
            <span className={`pill ${pillFor(w.last_run_status)}`}>{w.last_run_status}</span>
            <span className="muted small">{relativeTime(w.last_run_at)}</span>
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td className="mono">{w.run_count}</td>
      <td className="muted small">{relativeTime(w.updated_at)}</td>
      <td>
        <span className="row-tight" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onDuplicate}>Duplicate</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onDelete} style={{ color: 'var(--danger)' }}>Delete</button>
        </span>
      </td>
    </tr>
  )
}
